import { useEffect, useState } from "react";
import { QUALITY_META } from "@/hooks/useVideoFeed";
import { PROFILE_PHASE_LABEL } from "@/hooks/useVideoProfile";
import { Wifi, Radio, Cctv, Gauge, Activity, MonitorPlay, Film, Settings2, Loader2, AlertTriangle } from "lucide-react";

const S = { ok: "#22C55E", warn: "#F59E0B", off: "#EF4444", idle: "#A3A3A3" };

// Ordre d'affichage des profils vidéo (les inconnus vont à la fin).
export const PROFILE_ORDER = ["eco", "standard", "fluid", "quality", "hd-test", "max-res"];
// Libellés d'affichage (surcharge des labels du JSON Pi).
export const PROFILE_LABELS = { "hd-test": "Haute définition", "max-res": "4K" };
export const profileLabel = (id, p) => PROFILE_LABELS[id] || p?.label || id;

export const orderedProfileEntries = (list = {}) =>
  Object.entries(list).sort((a, b) => {
    const ia = PROFILE_ORDER.indexOf(a[0]); const ib = PROFILE_ORDER.indexOf(b[0]);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

// Sparkline SVG de latence (dernières mesures)
export const LatencySparkline = ({ data = [], width = 96, height = 24 }) => {
  if (!data || data.length < 2) return <div className="font-mono text-[9px] text-[#525252]">latence…</div>;
  const max = Math.max(...data, 1), min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = data[data.length - 1];
  const color = last <= 400 ? S.ok : last <= 1500 ? S.warn : S.off;
  return (
    <svg data-testid="latency-sparkline" width={width} height={height} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// Sélecteur de profil vidéo (Eco/Standard/Fluide/Qualité/HD test) + état UX
export const VideoProfileSelector = ({ vp }) => {
  const { profiles, current, phase, error, blocked, available, apply } = vp;
  if (!available) {
    return (
      <div data-testid="video-profile-unavailable" className="flex items-center gap-1.5 font-mono text-[9px] text-[#525252]">
        <Settings2 size={11} /> Profils vidéo indisponibles (Infra API v2.7 + fichiers profils requis)
      </div>
    );
  }
  const list = profiles?.profiles || {};
  const activeId = current?.profile;
  const busy = phase !== "idle" && phase !== "error";
  const phaseLabel = PROFILE_PHASE_LABEL[phase];
  return (
    <div data-testid="video-profile-selector" className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-[#A3A3A3]"><Settings2 size={11} /> Profil</span>
        {Object.entries(list).map(([id, p]) => (
          <button key={id} data-testid={`video-profile-${id}`} onClick={() => apply(id)} disabled={busy}
            title={p.usage || ""}
            className={`px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider transition-colors disabled:opacity-40 border ${activeId === id ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "text-white/60 border-white/15 hover:text-white"}`}>
            {p.label || id}
          </button>
        ))}
        {busy && <span className="flex items-center gap-1 font-mono text-[9px] text-[#3B82F6]"><Loader2 size={11} className="animate-spin" /> {phaseLabel}</span>}
        {phase === "reconnected" && <span className="font-mono text-[9px] text-[#22C55E]">{phaseLabel}</span>}
      </div>
      {current && (
        <div data-testid="video-profile-active" className="font-mono text-[9px] text-[#A3A3A3]">
          Actif : <span className="text-white">{current.width}×{current.height}</span> · {current.fps} fps · {current.bitrate ? Math.round(current.bitrate / 1000) + " kbps" : "—"}
        </div>
      )}
      {blocked && (
        <div data-testid="video-profile-blocked" className="flex items-center gap-1.5 font-mono text-[9px] text-[#F59E0B]">
          <AlertTriangle size={11} /> {blocked.reason} — recommandé : {blocked.recommended}
        </div>
      )}
      {error && <div data-testid="video-profile-error" className="font-mono text-[9px] text-[#EF4444]">{error}</div>}
    </div>
  );
};

// Sélecteur de profil DISCRET, VERTICAL. Par défaut : fine colonne de pastilles (très sobre).
// Au survol : le panneau s'ouvre et les noms des profils apparaissent à droite de chaque pastille,
// profil actif surligné. Reste visible en plein écran. Routes profils existantes (vp.apply).
export const VideoProfileSlider = ({ vp }) => {
  const { profiles, current, phase, blocked, available, apply } = vp;
  if (!available) {
    return <div data-testid="video-profile-slider" className="pointer-events-auto bg-black/35 border border-white/10 rounded-lg backdrop-blur-md px-2 py-1 font-mono text-[8px] text-white/35">Profils indisponibles</div>;
  }
  const entries = orderedProfileEntries(profiles?.profiles || {});
  const activeId = current?.profile;
  const busy = phase !== "idle" && phase !== "error";
  return (
    <div data-testid="video-profile-slider"
      className="group pointer-events-auto bg-black/25 hover:bg-black/65 border border-white/[0.06] hover:border-white/20 rounded-lg backdrop-blur-md px-1.5 py-1.5 transition-all duration-200">
      <div className="flex flex-col gap-1">
        {entries.map(([id, p]) => {
          const on = id === activeId;
          return (
            <button key={id} data-testid={`profile-tick-${id}`} onClick={() => apply(id)} disabled={busy}
              title={profileLabel(id, p)}
              className="group/i flex items-center gap-2 disabled:opacity-40 text-left">
              <span className="shrink-0 w-2.5 flex items-center justify-center">
                <span className={`rounded-full transition-all ${
                  on ? "w-2 h-2 bg-[#3B82F6] ring-2 ring-[#3B82F6]/30"
                     : "w-1 h-1 bg-white/30 group-hover/i:w-1.5 group-hover/i:h-1.5 group-hover/i:bg-white"}`} />
              </span>
              <span data-testid={`profile-label-${id}`}
                className={`hidden group-hover:block whitespace-nowrap font-mono text-[9px] uppercase tracking-wide transition-colors ${
                  on ? "text-[#3B82F6] font-bold" : "text-white/55 hover:text-white"}`}>
                {profileLabel(id, p)}
              </span>
            </button>
          );
        })}
      </div>
      {busy && (
        <div className="mt-1 flex items-center gap-1 font-mono text-[7px] text-[#3B82F6]">
          <Loader2 size={8} className="animate-spin" /><span className="hidden group-hover:inline">{PROFILE_PHASE_LABEL[phase]}</span>
        </div>
      )}
      {blocked && <div data-testid="profile-slider-blocked" className="hidden group-hover:block mt-1 font-mono text-[8px] text-[#F59E0B] max-w-[150px]">{blocked.reason}</div>}
    </div>
  );
};

// Fenêtre compacte bas-droite : qualité + LAT + FPS + résolution + bitrate + zoom + profil actif.
// Alerte discrète : bordure orange (Moyen) / bordure rouge + pulsation (Dégradé).
export const CockpitVideoMetrics = ({ vf, vp, zoomLevel, testid = "cockpit-video-metrics" }) => {
  const q = vf?.quality;
  const cur = vp?.current;
  const res = vf?.videoStats?.width ? `${vf.videoStats.width}×${vf.videoStats.height}` : (cur ? `${cur.width}×${cur.height}` : "—");
  const fps = vf?.metrics?.fps != null ? vf.metrics.fps : (cur?.fps ?? "—");
  const lat = vf?.metrics?.latencyMs != null ? `${vf.metrics.latencyMs}ms` : "—";
  const kbps = vf?.videoStats?.bitrateKbps != null ? vf.videoStats.bitrateKbps : (cur?.bitrate ? Math.round(cur.bitrate / 1000) : null);
  const mbps = kbps != null ? `${(kbps / 1000).toFixed(1)} Mbps` : "—";
  const degraded = q === "degrade";
  const alert = q === "moyen" || degraded;
  const alertColor = alert ? QUALITY_META[q].color : undefined;
  return (
    <div data-testid={testid} data-quality-alert={alert ? q : "none"}
      className={`bg-black/50 border rounded-md px-2 py-1 backdrop-blur-md font-mono text-right leading-tight opacity-90 transition-colors ${degraded ? "animate-pulse" : ""}`}
      style={{ borderColor: alertColor || "rgba(255,255,255,0.10)", boxShadow: alert ? `0 0 8px ${alertColor}55` : undefined }}>
      {q && (
        <div className="flex items-center justify-end gap-1 text-[10px]" data-testid="metrics-quality" style={{ color: QUALITY_META[q].color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: QUALITY_META[q].color }} /> {QUALITY_META[q].label}
        </div>
      )}
      <div className="text-[9px] text-white/70" data-testid="metrics-lat"><span className="text-white/35">LAT</span> {lat}</div>
      <div className="text-[9px] text-white/70" data-testid="metrics-fps"><span className="text-white/35">FPS</span> {fps}</div>
      <div className="text-[8px] text-white/45" data-testid="metrics-res">{res}</div>
      <div className="text-[8px] text-white/45" data-testid="metrics-bitrate">{mbps}</div>
      <div className="text-[8px] text-[#2DD4BF]/80" data-testid="metrics-zoom">Zoom {(zoomLevel || 1).toFixed(1)}x</div>
      <div className="text-[8px] text-white/45" data-testid="metrics-profile">Profil {profileLabel(cur?.profile, cur)}</div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-2 py-0.5">
    <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-[#A3A3A3]"><Icon size={11} /> {label}</span>
    <span className="font-mono text-[10px]" style={{ color: color || "#fff" }}>{value}</span>
  </div>
);

// Panneau "Santé flux vidéo"
export const VideoHealthPanel = ({ vf }) => {
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((t) => t + 1), 1000); return () => clearInterval(id); }, []);
  const { playMode, metrics, quality, videoStats, reconnections, lastFrameAt, health, failReason, latencyHistory } = vf;
  const rtsp = health?.checks?.rtsp?.reachable;
  const webrtcUp = playMode === "webrtc" || !!health?.checks?.webrtc?.reachable;
  const whepState = playMode === "webrtc" ? "Connecté" : failReason === "webrtc_failed" ? "Échec" : "—";
  const whepColor = playMode === "webrtc" ? S.ok : failReason === "webrtc_failed" ? S.off : S.idle;
  const frameAge = lastFrameAt ? Math.max(0, Math.round((Date.now() - lastFrameAt) / 1000)) : null;
  const res = videoStats?.width && videoStats?.height ? `${videoStats.width}×${videoStats.height}` : "—";
  return (
    <div data-testid="video-health-panel" className="bg-[#141414] border border-white/10 rounded-sm">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10">
        <MonitorPlay size={12} className="text-[#A3A3A3]" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">Santé flux vidéo</span>
        <div className="ml-auto flex items-center gap-2">
          {quality && <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: QUALITY_META[quality].color }}>{QUALITY_META[quality].label}</span>}
          <span className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
            style={{ borderColor: (playMode ? S.ok : S.idle) + "66", color: playMode ? S.ok : S.idle }}>
            {playMode ? playMode.toUpperCase() : "INACTIF"}
          </span>
        </div>
      </div>
      <div className="p-3 grid grid-cols-2 gap-x-4">
        <div>
          <Row icon={Gauge} label="Latence" value={metrics?.latencyMs != null ? `${metrics.latencyMs} ms` : "—"} testid="vh-latency" />
          <Row icon={Film} label="FPS" value={metrics?.fps != null ? metrics.fps : "—"} testid="vh-fps" />
          <Row icon={Activity} label="Bitrate" value={videoStats?.bitrateKbps != null ? `${videoStats.bitrateKbps} kbps` : "—"} testid="vh-bitrate" />
          <Row icon={MonitorPlay} label="Résolution" value={res} testid="vh-res" />
        </div>
        <div>
          <Row icon={Radio} label="RTSP source" value={rtsp ? "OK" : "—"} color={rtsp ? S.ok : S.idle} testid="vh-rtsp" />
          <Row icon={Wifi} label="Relais MediaMTX" value={webrtcUp ? "OK" : "—"} color={webrtcUp ? S.ok : S.idle} testid="vh-relay" />
          <Row icon={Cctv} label="WHEP" value={whepState} color={whepColor} testid="vh-whep" />
          <Row icon={Activity} label="Reconnexions" value={reconnections ?? 0} color={reconnections ? S.warn : "#fff"} testid="vh-reconn" />
        </div>
      </div>
      <div className="px-3 pb-3 flex items-center justify-between gap-2 border-t border-white/5 pt-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-wider text-[#A3A3A3]">Latence</span>
          <LatencySparkline data={latencyHistory} />
        </div>
        <span data-testid="vh-lastframe" className="font-mono text-[9px] text-[#525252]">
          Dernier frame {frameAge != null ? `il y a ${frameAge}s` : "—"}
        </span>
      </div>
    </div>
  );
};
